import { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Card, Chip, CircularProgress, Divider, LinearProgress, Modal, ModalDialog,
    Option, Select, Sheet, Stack, Table, Typography,
} from '@mui/joy';

import { usePartner } from '../../PartnerContext';
import {
    APIPartnerTerritorySummary, APIPartnerTerritoryCities, APIPartnerTerritoryCapacity,
    APIPartnerTerritoryAds, APIPartnerTerritoryAdDetail,
} from '../../api_partner';
import AdStatusChip from '../Ads/AdStatusChip';
import { formatInt } from '../Ads/adsConstants';
import { rangeFor, PERIOD_OPTIONS } from '../partnerPeriod';

function Metric({ label, value }) {
    return (
        <Card variant="soft" sx={{ flex: 1, minWidth: 120, p: 1.5 }}>
            <Typography level="body-xs" color="neutral">{label}</Typography>
            <Typography level="h4">{value}</Typography>
        </Card>
    );
}

const CAP_STATUS = {
    free: { color: 'success', text: 'Есть свободный трафик' },
    busy: { color: 'warning', text: 'Территория загружена' },
    full: { color: 'danger', text: 'Свободного трафика нет' },
    unknown: { color: 'neutral', text: 'Недостаточно данных' },
};

function AdDetailModal({ token, campaignId, range, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        APIPartnerTerritoryAdDetail(token, campaignId, range)
            .then((r) => { if (alive) setData(r); })
            .catch(() => { if (alive) setData(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [token, campaignId, range]);

    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{ maxWidth: 640, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
                {loading ? (
                    <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
                ) : !data ? (
                    <Alert color="danger">Не удалось загрузить</Alert>
                ) : (
                    <>
                        <Typography level="h4">{data.campaign_name}</Typography>
                        <Stack direction="row" spacing={1} sx={{ my: 1 }} alignItems="center">
                            <AdStatusChip status={data.status} />
                            <Chip size="sm" variant="soft">{data.geo_scope_display}</Chip>
                            <Typography level="body-sm" color="neutral">{data.advertiser}</Typography>
                        </Stack>
                        <Stack direction="row" spacing={2} sx={{ my: 1 }}>
                            <Metric label="Показы" value={formatInt(data.impressions)} />
                            <Metric label="Клики" value={formatInt(data.clicks)} />
                            <Metric label="CTR" value={`${data.ctr}%`} />
                        </Stack>

                        {(data.creatives || []).length > 0 && (
                            <>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography level="title-sm" sx={{ mb: 1 }}>Объявления</Typography>
                                <Stack spacing={1}>
                                    {data.creatives.map((cr, i) => (
                                        <Sheet key={i} variant="soft" sx={{ p: 1, borderRadius: 'sm' }}>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                {cr.image_url && (
                                                    <img src={cr.image_url} alt="" width={56} height={56}
                                                         style={{ objectFit: 'cover', borderRadius: 6 }} />
                                                )}
                                                <Box>
                                                    <Typography level="body-sm" sx={{ fontWeight: 600 }}>{cr.title}</Typography>
                                                    <Typography level="body-xs" color="neutral">{cr.text}</Typography>
                                                </Box>
                                            </Stack>
                                        </Sheet>
                                    ))}
                                </Stack>
                            </>
                        )}

                        {(data.by_city || []).length > 0 && (
                            <>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography level="title-sm" sx={{ mb: 1 }}>По городам</Typography>
                                <Table size="sm">
                                    <thead><tr><th>Город</th><th>Показы</th><th>Клики</th></tr></thead>
                                    <tbody>
                                        {data.by_city.map((c, i) => (
                                            <tr key={i}><td>{c.city}</td><td>{formatInt(c.impressions)}</td><td>{formatInt(c.clicks)}</td></tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </>
                        )}
                    </>
                )}
            </ModalDialog>
        </Modal>
    );
}

export default function PartnerTerritory() {
    const { partnerToken: token } = usePartner();
    const [days, setDays] = useState(30);
    const [summary, setSummary] = useState(null);
    const [capacity, setCapacity] = useState(null);
    const [cities, setCities] = useState([]);
    const [ads, setAds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [detailId, setDetailId] = useState(null);

    const range = rangeFor(days);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        setError(null);
        try {
            const opts = rangeFor(days);
            const [s, cap, c, a] = await Promise.all([
                APIPartnerTerritorySummary(token, opts),
                APIPartnerTerritoryCapacity(token).catch(() => null),
                APIPartnerTerritoryCities(token, opts),
                APIPartnerTerritoryAds(token, { ...opts, pageSize: 50 }),
            ]);
            setSummary(s);
            setCapacity(cap);
            setCities(c?.items || []);
            setAds(a?.items || []);
        } catch (e) {
            setError('Не удалось загрузить данные территории');
        } finally {
            setLoading(false);
        }
    }, [token, days]);

    useEffect(() => { load(); }, [load]);

    const cap = capacity?.capacity;
    const capSt = CAP_STATUS[capacity?.status] || CAP_STATUS.unknown;

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography level="title-md">Реклама на вашей территории</Typography>
                <Select size="sm" value={days} onChange={(_, v) => setDays(v)} sx={{ minWidth: 130 }}>
                    {PERIOD_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                </Select>
            </Stack>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
                <>
                    {summary && (
                        <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
                            <Metric label="Показы" value={formatInt(summary.impressions)} />
                            <Metric label="Клики" value={formatInt(summary.clicks)} />
                            <Metric label="CTR" value={`${summary.ctr}%`} />
                            <Metric label="Рекламодателей" value={formatInt(summary.advertisers)} />
                            <Metric label="Кампаний" value={formatInt(summary.campaigns)} />
                        </Stack>
                    )}

                    {cap && (
                        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', mb: 2 }}>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                <Typography level="body-sm">Загрузка территории</Typography>
                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>{cap.fill_percent}%</Typography>
                            </Stack>
                            <LinearProgress determinate value={Math.min(100, cap.fill_percent)} color={capSt.color} />
                            <Typography level="body-xs" color="neutral" sx={{ mt: 1 }}>
                                {capSt.text}. Свободно ~{formatInt(cap.free_per_day)} показов/сут.
                            </Typography>
                        </Sheet>
                    )}

                    <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto', mb: 2 }}>
                        <Table hoverRow sx={{ '--TableCell-paddingY': '10px' }}>
                            <thead>
                                <tr>
                                    <th>Реклама</th>
                                    <th style={{ width: 160 }}>Рекламодатель</th>
                                    <th style={{ width: 120 }}>Статус</th>
                                    <th style={{ width: 90 }}>Показы</th>
                                    <th style={{ width: 90 }}>Клики</th>
                                </tr>
                            </thead>
                            <tbody>
                                {ads.map((a) => (
                                    <tr key={a.campaign_id} style={{ cursor: 'pointer' }}
                                        onClick={() => setDetailId(a.campaign_id)}>
                                        <td>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {a.sample_creative?.image_url && (
                                                    <img src={a.sample_creative.image_url} alt="" width={40} height={40}
                                                         style={{ objectFit: 'cover', borderRadius: 6 }} />
                                                )}
                                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                                    {a.sample_creative?.title || a.campaign_name}
                                                </Typography>
                                            </Stack>
                                        </td>
                                        <td><Typography level="body-sm">{a.advertiser}</Typography></td>
                                        <td><AdStatusChip status={a.status} /></td>
                                        <td>{formatInt(a.impressions)}</td>
                                        <td>{formatInt(a.clicks)}</td>
                                    </tr>
                                ))}
                                {ads.length === 0 && (
                                    <tr><td colSpan={5}>
                                        <Typography level="body-sm" color="neutral" sx={{ py: 3, textAlign: 'center' }}>
                                            На вашей территории пока не было рекламы
                                        </Typography>
                                    </td></tr>
                                )}
                            </tbody>
                        </Table>
                    </Sheet>

                    {cities.length > 0 && (
                        <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                            <Typography level="title-sm" sx={{ p: 1.5, pb: 0.5 }}>По городам территории</Typography>
                            <Table size="sm" sx={{ '--TableCell-paddingY': '8px' }}>
                                <thead><tr><th>Город</th><th>Показы</th><th>Клики</th><th>CTR</th></tr></thead>
                                <tbody>
                                    {cities.map((c, i) => (
                                        <tr key={i}>
                                            <td>{c.city}</td><td>{formatInt(c.impressions)}</td>
                                            <td>{formatInt(c.clicks)}</td><td>{c.ctr}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>
                    )}
                </>
            )}

            {detailId && (
                <AdDetailModal token={token} campaignId={detailId} range={range}
                               onClose={() => setDetailId(null)} />
            )}
        </Box>
    );
}